import { motion } from 'framer-motion'
import {
  Shield,
  Clock,
  Award,
  Users
} from 'lucide-react'
import HeroSection from '../components/HeroSection'
import ServicesPreview from '../components/ServicesPreview'
import WhyChooseUs from '../components/WhyChooseUs'
import Testimonials from '../components/Testimonials'
import CTASection from '../components/CTASection'

const Home = () => {
  const features = [
    {
      icon: Shield,
      title: 'Антикор Брест - Гарантия качества',
      description: 'Антикор Брест предоставляет гарантию на все виды антикоррозийной обработки от 1 до 5 лет'
    },
    {
      icon: Clock,
      title: 'Быстрое выполнение',
      description: 'Полная обработка автомобиля за 1-2 дня в зависимости от размера'
    },
    {
      icon: Award,
      title: 'Сертифицированные материалы',
      description: 'Используем только качественные материалы ведущих производителей'
    },
    {
      icon: Users,
      title: 'Опытные мастера',
      description: 'Команда профессионалов с опытом работы более 10 лет'
    }
  ]

  const processSteps = [
    {
      number: '01',
      title: 'Диагностика',
      description: 'Осмотр автомобиля и выявление проблемных зон'
    },
    {
      number: '02',
      title: 'Подготовка',
      description: 'Тщательная очистка и обезжиривание поверхностей'
    },
    {
      number: '03',
      title: 'Обработка',
      description: 'Нанесение защитных составов в несколько слоев'
    },
    {
      number: '04',
      title: 'Контроль',
      description: 'Проверка качества '
    }
  ]

  return (
    <div className="page-padding">
      {/* Hero Section */}
      <HeroSection />

      {/* Features Section */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Почему выбирают нас
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Мы предлагаем профессиональные услуги по антикоррозийной обработке
              с использованием современных технологий и качественных материалов
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href="/anticor-brest" className="text-primary-600 font-semibold underline">
              Антикор Брест — перейти на посадочную страницу
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Как проходит работа
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Процесс антикоррозийной обработки состоит из нескольких этапов,
              каждый из которых важен для достижения качественного результата
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-secondary-600">
                    {step.description}
                  </p>
                </div>

                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 transform translate-x-4" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <ServicesPreview />

      {/* Experience with Chinese Brands */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Специализация по китайским маркам
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Мы обладаем уникальным опытом работы с китайскими автомобилями
              и знаем все особенности их антикоррозийной защиты
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-6">
                Экспертиза в области китайского автопрома
              </h3>
              <p className="text-lg text-secondary-600 mb-6 leading-relaxed">
                Наша команда специализируется на антикоррозийной обработке
                автомобилей китайских марок. Мы изучили все особенности
                конструкции и материалов, используемых в Geely, Belgee и BYD.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Geely</h4>
                    <p className="text-secondary-600 text-sm">
                      Опыт работы с моделями Atlas, Coolray, Tugella. Знаем особенности
                      оцинкованных кузовов и специфику обработки.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">Belgee</h4>
                    <p className="text-secondary-600 text-sm">
                      Профессиональная обработка белорусско-китайских автомобилей.
                      Учитываем особенности местных дорожных условий.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-secondary-900 mb-1">BYD</h4>
                    <p className="text-secondary-600 text-sm">
                      Специализация по электрическим и гибридным моделям.
                      Особые требования к защите электронных компонентов.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Почему это важно?
                </h4>
                <p className="text-secondary-600 text-sm">
                  Китайские автомобили имеют свои особенности конструкции и материалов.
                  Наш опыт позволяет подобрать оптимальные составы и технологии
                  для максимальной защиты именно вашего автомобиля.
                </p>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-secondary-900 mb-6 text-center">
                  Наши преимущества
                </h4>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-sm">✓</span>
                    </div>
                    <span className="text-secondary-700">Знание особенностей китайских марок</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-sm">✓</span>
                    </div>
                    <span className="text-secondary-700">Специальные составы для китайских авто</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-sm">✓</span>
                    </div>
                    <span className="text-secondary-700">Опыт работы с электронными системами</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-sm">✓</span>
                    </div>
                    <span className="text-secondary-700">Гарантия качества для китайских марок</span>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg">
                  <p className="text-sm text-secondary-600 text-center">
                    <span className="font-semibold text-primary-600">100+</span> автомобилей
                    китайских марок успешно обработано
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* Testimonials */}
      <Testimonials />

      {/* CTA Section */}
      <CTASection />
    </div>
  )
}

export default Home
